"use client";

import React, { useEffect, useRef, useState } from "react";
import { Brain, Activity, HeartPulse, PersonStanding, ShieldCheck, Stethoscope, CircleCheck, Leaf, Sprout, TreePine } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>(threshold = 0.1) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(node);
      }
    }, {
      threshold
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return {
    ref,
    isVisible
  };
}
const methods = [{
  title: "Hypnosystemik",
  icon: Brain,
  p1: "In der Tradition von Gunther Schmidt nutzen wir die Synergie aus Erickson’scher Hypnotherapie und systemischem Konstruktivismus. Wir fokussieren uns auf die Reaktivierung unbewusster Kompetenz-Netzwerke, um einschränkende Problem-Trancen aufzulösen.",
  p2: "Durch gezielte Utilisation Ihrer Ressourcen transformieren wir festgefahrene Muster in autonome Handlungsspielräume, die weit über rein kognitives Verstehen hinausgehen.",
  benefits: ["Transformation von Problem-Trancen", "Aktivierung unbewusster Potentiale"]
}, {
  title: "Systemische Lösungsorientierung",
  icon: Activity,
  p1: "Hier rücken wir die Interaktionsdynamiken und Ihre individuellen Lösungs-Ausnahmen in das Zentrum der Betrachtung. Probleme werden nicht als Defizit, sondern als Lösungsversuche in komplexen Systemzusammenhängen verstanden.",
  p2: "Wir erarbeiten zirkuläre Perspektiven und konkrete Skalierungen, die Ihre Selbstwirksamkeit stärken und nachhaltige Verhaltensänderungen in Ihrem sozialen oder beruflichen Feld initiieren.",
  benefits: ["Zirkuläre Kontextanalyse", "Stärkung der Selbstwirksamkeit"]
}, {
  title: "Achtsamkeit & Metakognition",
  icon: HeartPulse,
  p1: "Basierend auf der Insight Meditation nach Christopher Titmuss schulen wir die Fähigkeit zur metakognitiven Beobachtung. Dies ermöglicht die Distanzierung von automatisierten Gedankenströmen und affektiven Stress-Kaskaden.",
  p2: "Ziel ist die neurobiologische Selbstregulation und die Etablierung einer inneren Präsenz, die auch in hochgradig belastenden Situationen die psychische Flexibilität bewahrt.",
  benefits: ["Metakognitive Distanzierung", "Neurobiologische Regulation"]
}, {
  title: "Embodiment & Somatik",
  icon: PersonStanding,
  p1: "Unter Einbeziehung von Somatic-Marker-Theorien nutzen wir Tai Ji, Qi Gong und Yoga als Bottom-up-Verfahren. Körperhaltungen und Bewegungsabläufe werden direkt als Feedbackschleifen für mentale Prozesse genutzt.",
  p2: "Die Erfahrung von Zentrierung und Balance wird auf zellulärer Ebene verankert, wodurch neue psychische Haltungen durch physische Repräsentanzen im Nervensystem stabilisiert werden.",
  benefits: ["Vagus-Nerv-Regulation", "Somatische Ressourcenverankerung"]
}, {
  title: "Natur als Co-Therapeut",
  icon: ShieldCheck,
  p1: "Die Wildnispädagogik nutzt den Naturraum Soonwald als externen Regulator. Die biophile Resonanz wirkt unmittelbar dämpfend auf das sympathische Nervensystem und öffnet Räume für projektive Erkenntnisprozesse.",
  p2: "In der Stille der Natur finden wir archetypische Metaphern für Ihre persönliche Transformation, die eine tiefgreifende Erdung und Rekalibrierung Ihrer innerer Kompassnadel ermöglichen.",
  benefits: ["Biophile Stressreduktion", "Projektive Naturerfahrung"]
}, {
  title: "Krisenintervention",
  icon: Stethoscope,
  p1: "In akuten Belastungssituationen erfolgt eine sofortige psychologische Stabilisierung zur Prävention von Sekundärtraumatisierungen oder Burnout-Eskalationen. Wir sortieren das kognitive Chaos durch professionelle Strukturierungshilfen.",
  p2: "Die Begleitung zielt auf die rasche Wiederherstellung der Affektregulation und die Erarbeitung kurzfristiger Bewältigungsstrategien zur Sicherung Ihrer psychischen Integrität ab.",
  benefits: ["Akute Affektregulation", "Burnout-Prävention"]
}];
const phases = [{
  title: "Sofort-Entlastung",
  description: "In der ersten Phase schaffen wir Raum für Entlastung. Wir identifizieren akute Stressoren, und ich zeige dir sofort wirksame Stabilisierungstechniken, um das Nervensystem zu beruhigen und das kognitive Chaos zu ordnen.",
  icon: Leaf
}, {
  title: "Neuausrichtung",
  description: "Wir blicken hinter das Offensichtliche. Durch systemisch-lösungsorientierte und\xA0 hypnosystemische Methoden entdecken wir unbewusste Ressourcen und entwerfen neue Lösungswege jenseits alter Muster.",
  icon: Sprout
}, {
  title: "Umsetzen",
  description: "Die Theorie wird zur Praxis. Wir integrieren neue Verhaltensmuster durch gezielte Embodiment-Übungen und Transfer-Aufgaben direkt in deinen individuellen Alltag.",
  icon: TreePine
}, {
  title: "Resilienz stärken",
  description: "Nachhaltigkeit ist das Ziel. Wir festigen deine psychische Widerstandskraft und verankern neuen Kompetenzen, damit du künftigen Herausforderungen mit innerer Stärke begegnen kannst.",
  icon: ShieldCheck
}];
function MethodCard({
  method,
  index
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll(0.1);
  const Icon = method.icon;
  return <div ref={ref} className={`transition-all duration-1000 ease-out delay-${index * 100} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="flex flex-col items-center group">
        <div className="mx-auto w-24 h-24 rounded-full bg-accent/30 flex items-center justify-center mb-8 transition-all duration-700 group-hover:bg-primary/10 group-hover:scale-110">
          <Icon className="w-10 h-10 text-primary/70 group-hover:text-primary transition-colors" strokeWidth={1} />
        </div>
        <h3 className="text-2xl font-serif font-light text-foreground mb-6 text-center tracking-wide uppercase">
          {method.title}
        </h3>
        <div className="w-8 h-[1px] bg-primary/30 mb-6 group-hover:w-16 transition-all duration-500" />
        <div className="space-y-4 text-center text-muted-foreground leading-relaxed font-sans font-light text-[17px]">
          <p>{method.p1}</p>
          <p>{method.p2}</p>
        </div>
        <div className="pt-6 mt-auto w-full">
          <ul className="flex flex-col items-center gap-3">
            {method.benefits.map((benefit, idx) => <li key={idx} className="flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/60 font-sans">
                <CircleCheck className="size-3 text-primary/50" />
                {benefit}
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
}
function PhaseCard({
  phase,
  index,
  phasesVisible
}) {
  const Icon = phase.icon;
  const isEven = index % 2 === 0;
  return <div data-index={index} className={`relative flex items-center w-full transition-all duration-1000 ease-out delay-${index * 200} ${phasesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 flex items-center justify-center z-20">
        <div className="size-10 md:size-12 rounded-full border-2 border-primary bg-background flex items-center justify-center shadow-sm">
          <span className="font-sans font-medium text-primary text-xs md:text-sm">0{index + 1}</span>
        </div>
      </div>

      {}
      <div className={`w-full pl-16 md:pl-0 md:w-[45%] ${isEven ? 'md:mr-auto' : 'md:ml-auto'}`}>
        <Card className={`bg-card/40 backdrop-blur-sm border-none shadow-md hover:shadow-lg transition-all duration-500 rounded-2xl p-6 md:p-8 ${isEven ? 'md:text-right' : 'md:text-left'}`}>
          <div className={`flex items-center gap-4 mb-3 ${isEven ? 'md:flex-row-reverse' : 'flex-row'}`}>
            <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
              <Icon className="size-5" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-xl md:text-2xl text-foreground font-medium tracking-wide uppercase">
              {phase.title}
            </h3>
          </div>
          <CardDescription className="font-sans text-base text-muted-foreground leading-relaxed font-light">
            {phase.description}
          </CardDescription>
        </Card>
      </div>
    </div>;
}
export default function MeinAnsatz() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll();
  const {
    ref: phasesRef,
    isVisible: phasesVisible
  } = useRevealOnScroll();
  return <div className="bg-background text-foreground">
      <section id="mein-ansatz-methoden" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.03] mix-blend-multiply" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }} aria-hidden="true" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div ref={headerRef} className={`max-w-4xl mx-auto text-center mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <span className="inline-block py-1 px-3 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-widest uppercase mb-6">Methodik & Expertise</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-8 text-foreground leading-tight font-light">
              <span className="italic text-primary">Klarheit</span> in der Stille.<br />
              <span className="italic text-primary">Lösungen</span> in Bewegung.
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed font-sans font-light max-w-2xl mx-auto">Wirksame Veränderung braucht ein Zusammenspiel von Denken, Fühlen und Wollen. Ich nutze darum sowohl Gesprächstechniken, als auch neurobiologische und körperorientierte Ansätze. Die Natur als Behandlungsraum liefert dazu einen wirkungsvollen Rahmen.&nbsp;</p>
            <div className="mt-10 flex flex-wrap justify-center gap-6">
              <Button asChild className="rounded-full px-10 py-4 h-auto transition-all hover:shadow-lg hover:-translate-y-1">
                <Link to="#phasen-des-wandels">Krisenberatung</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full px-10 py-4 h-auto border-primary text-primary hover:bg-primary/10 transition-all hover:shadow-lg hover:-translate-y-1">
                <Link to="#methodische-praezision">Methoden</Link>
              </Button>
            </div>
          </div>

          
        </div>
      </section>

      <section id="phasen-des-wandels" className="bg-background py-24 md:py-32 relative overflow-hidden border-t border-border/40">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-serif mb-6 text-foreground leading-tight font-light">
              Gut beraten in der <span className="text-primary italic">Krise</span>
            </h2>
            <p className="text-muted-foreground font-sans text-lg max-w-xl mx-auto font-light leading-relaxed">Vielleicht steckst Du gerade in einer akut schwierigen Lebenssituation: Burnout, Umbruchphasen, Liebeskummer, Scheidung, Jobverlust, ein belastender Konflikt, eine schwerwiegende Entscheidung. Ich unterstütze Dich dabei, Dich zunächst akut zu entlasten, um dann Klarheit und neue Perspektiven zu gewinnen, und schließlich als Mensch gereift aus der Situation hervorzugehen.&nbsp;</p>
          </div>

          <div ref={phasesRef} className="relative max-w-5xl mx-auto">
            {}
            <div className="absolute left-5 md:left-1/2 top-4 bottom-4 w-px bg-primary/20 md:-translate-x-1/2" />
            
            <div className="space-y-12 md:space-y-20">
              {phases.map((phase, i) => <PhaseCard key={i} phase={phase} index={i} phasesVisible={phasesVisible} />)}
            </div>

            {}
            <div className={`mt-20 text-center transition-all duration-1000 delay-1000 ${phasesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <Link to="mailto:praxis@claudiachrist.de" className="inline-flex items-center justify-center px-10 py-4 bg-primary text-primary-foreground rounded-full font-sans font-medium transition-all hover:bg-primary/90 hover:shadow-lg hover:-translate-y-1">
                Gespräch vereinbaren
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
}